import { ICareMap } from '../caremap/ICareMap';
import { InnerhivePage } from '../InnerhivePage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { CareMapCollaboratorList } from '../share-caremap/CareMapCollaboratorList';
import { IonButtons, IonCardContent, IonToolbar } from '@ionic/react';
import { HeaderPopoverTitle } from '../components/HeaderPopover';
import { ViewShareLinkButton } from '../share-caremap/ViewShareLinkButton';
import { createUseStyles } from 'react-jss';
import { Text } from '../components/Text';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import React, { useEffect } from 'react';
import { AddViewerButton } from './AddViewerButton';
import { InviteCoOwnerButton } from '../share-caremap/InviteCoOwnerButton';
import { RemoveMeButton } from './RemoveMeButton';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { CommunityModelFactory } from '../share-caremap/CommunityModelFactory';
import { AlertPresenter } from '../AlertBinder';
import { MenuBackButton } from '../components/MenuBackButton';

export const CommunityManagementPage: React.FC<{ careMapId: string; }> = ({ careMapId }) => {
    const caremapState = useInstance(CareMapState);
    useSubscription(() => caremapState);

    return (
        <ActiveCareMapProviderV2 careMapId={careMapId}>
            <CommunityManagementPageWithCaremap />
        </ActiveCareMapProviderV2>
    );
};

const useStyles = createUseStyles({
    contentContainer: {
        '& ion-card-content': {
            paddingTop: 32,
            paddingBottom: 5,

            '&:first-of-type': {
                paddingTop: 5
            },

            '&:last-of-type': {
                paddingBottom: 32
            }
        }
    },

    buttonWrapper: {
        paddingLeft: 10,
        paddingRight: 10,
        paddingBottom: 10
    }
});

const CommunitySectionHeader: React.FC<{ section: 'viewers' | 'co-owners'}> = ({ section }) => {
    const title = section === 'viewers' ? 'Viewers' : 'Co-Owners';
    const classes = useStyles();

    return (
        <IonCardContent>
            <h2 style={{fontWeight: '600'}}>{title}</h2>
            <Text type="subdetail">
                {section === 'viewers' && (
                    <>
                        Keep your community up to date! Add viewers to subscribe them to updates and give them view access.
                    </>
                )}
                {section === 'co-owners' && (
                    <>
                        Share your innerhive with at least one person who can help with notes, appointments and more. Co-owners have full access and also receive community updates.
                    </>
                )}
            </Text>
            <div className={classes.buttonWrapper} style={{paddingTop: 10}}>
                {section === 'viewers' && (
                    <AddViewerButton />
                )}
                {section === 'co-owners' && (
                    <InviteCoOwnerButton />
                )}
            </div>
        </IonCardContent>
    );
};

const CommunityManagementPageWithCaremap: React.FC = () => {
    const history = useInstance(HistoryViewModel);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const alert = useInstance(AlertPresenter);
    const caremapState = useInstance(CareMapState);
    useSubscription(() => caremapState);

    const caremap = caremapState.getActiveCaremap() as ICareMap;
    const orgState = useInstance(OrganizationsState);
    useSubscription(() => orgState);

    const factory = useInstance(CommunityModelFactory);
    useSubscription(() => factory);
    const community = factory.forActiveCaremap();

    const patientFirstName = caremapState.getPatientFirstName(caremap);

    const title = `${patientFirstName}'s Community`;

    // load data on mount
    useEffect(() => {
        community.load().catch(err => alert.showAndLogError(err));
    }, [community, alert]);

    const classes = useStyles();
    const hasPermissionsLink = Boolean(caremap.links.permissions);
    const hasCreateViewerAccessTokenLink = Boolean(caremap.links.createViewerAccessToken);

    useEffect(() => {
        mixpanel.track('Manage Community Opened');
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <InnerhivePage title={title}>
            {/* the back button pushes things down, so let's align the top with the feed */}
            <IonToolbar style={{marginTop: -16}}>
                <div style={{display: 'flex', alignItems: 'center'}}>
                    <IonButtons slot="start">
                        <MenuBackButton onClick={() => history.push(`/community/${caremap.careMapId}/feed`)} />
                        <span style={{paddingBottom: 2}}><Text type="title">Community Management</Text></span>
                    </IonButtons>
                </div>
            </IonToolbar>

            <div className={classes.contentContainer}>
                {hasPermissionsLink && (
                    <>
                        <CommunitySectionHeader section="viewers" />
                        <CareMapCollaboratorList section="viewers" />

                        <CommunitySectionHeader section="co-owners" />
                        <CareMapCollaboratorList section="co-owners" />
                    </>
                )}


                {hasCreateViewerAccessTokenLink && (
                    <IonCardContent>
                        <HeaderPopoverTitle title="Share Temporary Access" />
                        <div className={classes.buttonWrapper}>
                            <ViewShareLinkButton />
                        </div>
                    </IonCardContent>
                )}

                {community.canRemoveSelf() && (
                    <IonCardContent style={{paddingBottom: 24}}>
                        <h2 style={{fontWeight: '600'}}>Remove Me From This Innerhive</h2>
                        <Text type="subdetail">
                            By removing yourself you will no long be able to interact with or view information on {caremapState.getPatientFirstName()}'s innerhive.
                        </Text>
                        <div className={classes.buttonWrapper}>
                            <RemoveMeButton />
                        </div>
                    </IonCardContent>
                )}
            </div>
        </InnerhivePage>
    );
};
